import { ReactElement } from 'react';
import { defaultAnimateLayoutChanges, useSortable } from '@dnd-kit/sortable';
import type { AnimateLayoutChanges } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classnames from 'classnames/bind';
import MenuItemCard, { IMenuItemCardProps } from './MenuItemCard';
import styles from './SortableMenuItemCard.module.scss';

const cx = classnames.bind(styles);

const animateLayoutChanges: AnimateLayoutChanges = (args) =>
  args.containerId === args.previousContainerId ? defaultAnimateLayoutChanges(args) : false;

interface ISortableMenuItemCardProps extends IMenuItemCardProps {
  sectionId: string;
}

const SortableMenuItemCard = (props: ISortableMenuItemCardProps): ReactElement => {
  const { itemId, sectionId, name } = props;
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: itemId,
    data: { sectionId },
    animateLayoutChanges,
  });

  return (
    // onDragStart suppresses the browser's native anchor drag: the card contains a Link, and a
    // native link-drag steals pointer capture from dnd-kit mid-gesture.
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cx('sortable-card', { dragging: isDragging })}
      data-testid={`sortable-card-${itemId}`}
      {...listeners}
      onDragStart={(event) => event.preventDefault()}
    >
      {/* dnd-kit's KeyboardSensor only starts a drag when event.target is the activator node, so
          giving the activator its own button is what lets Enter on the inner Link still navigate. */}
      <button
        type="button"
        ref={setActivatorNodeRef}
        className={styles['drag-handle']}
        data-testid={`drag-handle-${itemId}`}
        {...attributes}
        aria-label={`Reorder ${name}`}
      >
        Reorder
      </button>
      <MenuItemCard {...props} />
    </div>
  );
};

export default SortableMenuItemCard;
